import { z } from 'zod';
import {
  ALL_PRODUCT_CATEGORIES,
  ALL_TENANT_ROLES,
  ALL_UNITS_OF_MEASURE,
} from './enums';
import { ALL_LABEL_BAIXA_KINDS, LABEL_BAIXAS_MAX } from './labelMutability';

/**
 * API REST pública readonly (issue #263) — integração com sistemas de
 * terceiros (primeiro caso: foodgestor).
 *
 * Modelo de autenticação (OAuth2 client-credentials simplificado):
 *
 *  1. O super-admin cria uma **integração** em `apps/admin` → `/settings/api-keys`.
 *     Ela nasce com `clientId` (legível, exibível) e um `clientSecret` que
 *     aparece UMA única vez na tela de criação — só o sha256 persiste.
 *  2. O parceiro troca `client_id` + `client_secret` em `POST /v1/token` por um
 *     access token de 24h. Cada chamada emite um token NOVO; os anteriores
 *     seguem válidos até o próprio vencimento, com teto de
 *     `MAX_ACTIVE_TOKENS_PER_KEY` ativos (o excedente derruba o mais antigo).
 *     Isso mantém parceiro multi-instância funcionando sem derrubar requests
 *     em voo, sem precisar guardar o token em claro.
 *  3. Os GETs autenticam SÓ por `Authorization: Bearer <token>` — o secret de
 *     longa duração nunca circula nos endpoints de dados (logs, proxies, APM).
 *
 * Nenhuma credencial em claro persiste: `api_keys.clientSecretHash` é sha256 e
 * os tokens vivem em `api_tokens/{sha256(token)}` (o docId É o hash).
 */

/** Prefixo do `clientId` (exibível no admin e nos logs do parceiro). */
export const API_CLIENT_ID_PREFIX = 'ec';
/** Prefixo do `clientSecret` — credencial de longa duração. */
export const API_CLIENT_SECRET_PREFIX = 'ec_secret_';
/** Prefixo do access token de 24h. */
export const API_TOKEN_PREFIX = 'ec_tok_';

/** Validade do access token emitido por `POST /v1/token`. */
export const API_TOKEN_TTL_SECONDS = 24 * 60 * 60;

/**
 * Teto de tokens simultaneamente válidos por integração. Ao emitir o
 * (N+1)-ésimo, o mais antigo é apagado. Cobre parceiro rodando em várias
 * instâncias (cada uma com seu token) sem deixar credencial acumulando.
 */
export const MAX_ACTIVE_TOKENS_PER_KEY = 5;

/**
 * Validade do `nextPageToken` da API pública (#795). Cursor mais velho que
 * isto é recusado com `invalid_page_token` e a listagem recomeça. 24 h, igual
 * ao token de acesso: uma paginação que dura mais que o token já exigiu
 * renová-lo, e um cursor eterno era uma entrada assinada que nunca morria —
 * nem quando o formato mudasse. Usado por
 * `apps/functions/src/publicApi/cursor.ts` e pela prosa do portal
 * (`scripts/gen-portal-content.ts`).
 */
export const API_CURSOR_TTL_MS = 24 * 60 * 60 * 1000;

/** Paginação dos endpoints de lista. */
export const API_DEFAULT_PAGE_SIZE = 50;
export const API_MAX_PAGE_SIZE = 100;

/** Rate limit de GETs por integração (configurável por chave). */
export const API_DEFAULT_REQUESTS_PER_MINUTE = 300;
export const API_MIN_REQUESTS_PER_MINUTE = 10;
export const API_MAX_REQUESTS_PER_MINUTE = 1000;

/**
 * Emissão de token: janela e teto, aplicados DUAS vezes — por integração
 * (pega parceiro bugado em loop; conta as emissões da credencial correta) e
 * por IP, contando e barrando SÓ as falhas (pega quem sonda variando
 * client_id). O teto de tokens ativos sozinho não bastaria: sem isto, um
 * cliente em loop criaria e apagaria tokens indefinidamente, queimando
 * Firestore.
 *
 * Desde a #795 (API-1, 2026-10-03) a janela por IP é consultada só depois de
 * a credencial ser recusada: atrás do Hosting o IP é o do edge do Google
 * (#268), e contá-la antes deixava falhas alheias barrarem o parceiro com o
 * secret correto. Usado por `apps/functions/src/publicApi/handlers/handleToken.ts`
 * e pela prosa do portal (`scripts/gen-portal-content.ts`).
 */
export const API_TOKEN_RATE_LIMIT = { maxPerWindow: 10, windowMs: 10 * 60 * 1000 } as const;

/** Janela do rate limit dos GETs. */
export const API_REQUEST_RATE_WINDOW_MS = 60 * 1000;

/**
 * Teto de ABUSO ANÔNIMO nas rotas de dados — não é cota de cliente.
 *
 * O teto por integração (`requestsPerMinute`) só é consultado DEPOIS de
 * autenticar, então quem manda bearer inválido nunca chegava a limite algum:
 * retornava 401 tendo já custado 1 leitura (`api_tokens`) + 1 escrita
 * (`api_requests`) num endpoint `invoker: 'public'`. Escrita ilimitada no
 * Firestore sem credencial, e o lixo ainda inflava a base de contagem do
 * próprio rate limit. Medido em produção (#452): um terço da amostra de
 * `api_requests` veio de quem não tinha credencial.
 *
 * A janela conta SÓ as linhas sem `keyId`. Um teto por IP puro puniria
 * parceiro legítimo atrás de NAT — todo o tráfego autenticado da mesma origem
 * cairia no mesmo balde. Assim o volume legítimo de um parceiro nunca enche o
 * próprio teto.
 *
 * Desde a #795 (API-1, 2026-10-03) o teto é consultado DEPOIS de autenticar
 * e só para quem falhou: antes ele vinha primeiro e, atrás do Hosting, o IP é
 * o do edge do Google (#268) — falhas alheias no mesmo edge davam 429 a
 * parceiro com bearer válido. O custo da troca é 1 leitura de `api_tokens`
 * por request anônimo, mesmo barrado. O que segue compartilhado é o balde das
 * FALHAS: quem manda token vencido atrás de um edge saturado pode ver 429 em
 * vez de `token_expired` até a janela drenar. Usado por
 * `apps/functions/src/publicApi/process.ts`.
 */
export const API_ANONYMOUS_IP_RATE_LIMIT = {
  maxPerWindow: 30,
  windowMs: 60 * 1000,
} as const;

/** Retenção do log de uso `/api_requests` (também base do rate limit). */
export const API_REQUEST_LOG_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Permissões da integração. Um scope por recurso, no formato
 * `<recurso>:<ação>` — o sufixo deixa espaço para `:write` sem trocar o
 * conceito.
 *
 * Origem: #263 — 2026-06 (API pública v1).
 *
 * Por quê: o mesmo scope de leitura autoriza o REST (`GET /v1/...`) e o
 * webhook do recurso (#792) — receber `product.updated` é ler o produto, só
 * que empurrado. Por isso os quatro scopes novos (`suppliers`, `printers`,
 * `templates`, `responsibles`) nasceram com os webhooks: são recursos sem rota
 * REST ainda, mas com evento. Não há `webhooks:manage` — a gestão das
 * assinaturas é só pelo portal (decisão do owner, #792, 2026-10-02).
 *
 * Usado por: `ApiKeySchema.scopes`; Functions `publicApi/process.ts` (rotas) e
 * `webhooks/fanout.ts` (`WEBHOOK_EVENT_CATALOG[type].scope`); admin
 * `ApiKeyEditorDialog` (checkbox por scope, i18n `admin.apiKeys.scopes.<recurso>Read`);
 * portal `WebhookEditorPage` (habilita só os eventos dos scopes da chave).
 *
 * Histórico:
 *  - 2026-10-02 12:50 +0200 — (#792) scopes de leitura dos recursos com webhook.
 */
export const ApiScope = {
  TENANTS_READ: 'tenants:read',
  PRODUCTS_READ: 'products:read',
  LABELS_READ: 'labels:read',
  USERS_READ: 'users:read',
  SUPPLIERS_READ: 'suppliers:read',
  PRINTERS_READ: 'printers:read',
  TEMPLATES_READ: 'templates:read',
  RESPONSIBLES_READ: 'responsibles:read',
} as const;
export type ApiScope = (typeof ApiScope)[keyof typeof ApiScope];
export const ALL_API_SCOPES = Object.values(ApiScope) as [ApiScope, ...ApiScope[]];
export const ApiScopeSchema = z.enum(ALL_API_SCOPES);

/**
 * Quais clientes (tenants) uma integração da API pública alcança.
 *
 * Origem: #785 — 2026-09-30 15:19 +0200 (substitui a lista `tenantIds` da #263).
 *
 * Por quê: a lista fixa de até 50 empresas era escolhida à mão no admin e
 * envelhecia a cada venda — empresa nova do parceiro ficava fora até alguém
 * editar a chave, e a que saía dele continuava legível. O alcance agora é uma
 * REGRA avaliada no servidor a cada request, contra `tenants/{id}.partner_id`
 * (o mesmo campo que `crmTenantAccess.ts` usa para o acesso do CRM):
 *
 *  - `ALL` — qualquer tenant da plataforma.
 *  - `NON_PARTNER` — tenants sem `partner_id` (venda direta, cortesia, demo).
 *  - `PARTNER` — tenants com `partner_id == ApiKey.scopePartnerId`.
 *
 * Contrato: valor desconhecido NEGA (fail-closed) em `canAccessTenant`. É
 * conceito próprio — não reaproveitar `CommercialMode.PARTNER` nem
 * `CrmRole.PARTNER`, que têm o mesmo literal e outro sentido (#761).
 *
 * Usado por: `ApiKeySchema`, `CreateApiKeyInputSchema`, `UpdateApiKeyInputSchema`;
 * Functions `publicApi/auth.ts` (`canAccessTenant`) e `handlers/listTenants.ts`;
 * admin `ApiKeyEditorDialog` e `ApiKeysPage`.
 *
 * Issues: #263 (origem da API), #785 (alcance por regra).
 */
export const ApiTenantScope = {
  ALL: 'ALL',
  NON_PARTNER: 'NON_PARTNER',
  PARTNER: 'PARTNER',
} as const;
export type ApiTenantScope = (typeof ApiTenantScope)[keyof typeof ApiTenantScope];
export const ALL_API_TENANT_SCOPES = Object.values(ApiTenantScope) as [
  ApiTenantScope,
  ...ApiTenantScope[],
];
export const ApiTenantScopeSchema = z.enum(ALL_API_TENANT_SCOPES);

/** Id de `crm_partners/{id}` — mesmo teto de `CrmPartnerSchema`/`tenants.partner_id`. */
const ScopePartnerIdSchema = z.string().trim().min(1).max(128);

/**
 * Teto de demos liberados à mão numa integração (#785, 2026-10-01). Bate com o
 * máximo de referências que um `getAll` de validação lê de uma vez sem custo
 * relevante, e é folgado: o parque tem poucas empresas de demonstração.
 */
export const MAX_DEMO_TENANTS_PER_KEY = 30;

/**
 * Demos liberados à mão numa integração (#785, 2026-10-01 13:52 +0200).
 *
 * SOMAM à regra (`tenantScope`): o parceiro testa a API numa empresa de
 * demonstração que não é dele. Cada id só vale enquanto `tenants/{id}.isDemo`
 * for `true` — conferido a cada request (`isGrantedDemoTenant` em
 * `apps/functions/src/publicApi/auth.ts`) e na gravação
 * (`assertDemoTenantsValid`). Sem `/` (resolveria subdocumento) e sem repetição.
 */
const DemoTenantIdsSchema = z
  .array(z.string().trim().min(1).max(128).regex(/^[^/]+$/, 'invalid_tenant_id'))
  .max(MAX_DEMO_TENANTS_PER_KEY)
  .refine((ids) => new Set(ids).size === ids.length, 'duplicate_tenant_id');

/**
 * Coerência entre `tenantScope` e `scopePartnerId`, compartilhada pelos inputs
 * das callables (#785): `PARTNER` exige o parceiro, os outros dois o proíbem —
 * um `scopePartnerId` sobrando num `ALL` seria lido por alguém, um dia, como
 * se restringisse alguma coisa.
 */
function refineTenantScope(
  value: { tenantScope?: ApiTenantScope | undefined; scopePartnerId?: string | null | undefined },
  ctx: z.RefinementCtx,
): void {
  const hasPartner = value.scopePartnerId !== undefined && value.scopePartnerId !== null;
  if (value.tenantScope === undefined) {
    if (hasPartner) {
      ctx.addIssue({ code: 'custom', path: ['tenantScope'], message: 'tenant_scope_required' });
    }
    return;
  }
  if (value.tenantScope === ApiTenantScope.PARTNER && !hasPartner) {
    ctx.addIssue({ code: 'custom', path: ['scopePartnerId'], message: 'partner_required' });
  }
  if (value.tenantScope !== ApiTenantScope.PARTNER && hasPartner) {
    ctx.addIssue({ code: 'custom', path: ['scopePartnerId'], message: 'partner_not_allowed' });
  }
}

/**
 * Códigos de erro do envelope `{ error: { code, message } }`. Fechados: o
 * parceiro programa contra o `code`, nunca contra a `message`.
 *
 * `not_found` cobre tanto "tenant não existe" quanto "esta integração não tem
 * acesso a esse tenant" — de propósito, para não revelar quais tenantIds
 * existem. A causa real vai só para o log interno.
 */
export const ApiErrorCode = {
  /** `POST /v1/token`: client_id desconhecido, secret errado, chave inativa ou revogada. */
  INVALID_CLIENT: 'invalid_client',
  /** Bearer ausente, malformado ou desconhecido. */
  INVALID_TOKEN: 'invalid_token',
  /** Bearer conhecido, mas fora da validade — o cliente deve pedir outro token. */
  TOKEN_EXPIRED: 'token_expired',
  /** IP de origem fora de `allowedIps`. */
  IP_NOT_ALLOWED: 'ip_not_allowed',
  /** A integração não tem o scope exigido pela rota. */
  INSUFFICIENT_SCOPE: 'insufficient_scope',
  /** Rota inexistente OU tenant fora do alcance da integração. */
  NOT_FOUND: 'not_found',
  /** Query string / body inválidos. */
  INVALID_REQUEST: 'invalid_request',
  /** `pageToken` adulterado, expirado no formato ou de outro escopo. */
  INVALID_PAGE_TOKEN: 'invalid_page_token',
  RATE_LIMITED: 'rate_limited',
  METHOD_NOT_ALLOWED: 'method_not_allowed',
  INTERNAL: 'internal',
} as const;
export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

/**
 * Status HTTP de cada código de erro. Faz parte do contrato: o parceiro
 * programa contra o par (status, `code`), então isto vive aqui, junto do enum,
 * e não na camada de resposta das Functions — é o que permite ao gerador do
 * OpenAPI (`scripts/gen-portal-content.ts`) documentar os erros sem importar
 * nada de `apps/functions`.
 *
 * `Record` completo em vez de `switch`: um código novo sem entrada aqui não
 * compila, mesma exaustividade de antes, e o mapa é enumerável.
 */
export const API_ERROR_HTTP_STATUS: Record<ApiErrorCode, number> = {
  [ApiErrorCode.INVALID_CLIENT]: 401,
  [ApiErrorCode.INVALID_TOKEN]: 401,
  [ApiErrorCode.TOKEN_EXPIRED]: 401,
  [ApiErrorCode.IP_NOT_ALLOWED]: 403,
  [ApiErrorCode.INSUFFICIENT_SCOPE]: 403,
  [ApiErrorCode.NOT_FOUND]: 404,
  [ApiErrorCode.METHOD_NOT_ALLOWED]: 405,
  [ApiErrorCode.INVALID_REQUEST]: 400,
  [ApiErrorCode.INVALID_PAGE_TOKEN]: 400,
  [ApiErrorCode.RATE_LIMITED]: 429,
  [ApiErrorCode.INTERNAL]: 500,
};

/**
 * Mensagem humana de cada erro. Ajuda de leitura, NÃO contrato: pode mudar sem
 * aviso, e o parceiro deve programar contra o `code`. Fica aqui para que a
 * documentação publicada mostre exatamente o texto que a API devolve.
 */
export const API_ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  [ApiErrorCode.INVALID_CLIENT]:
    'Unknown client_id, wrong client_secret, or the integration is inactive.',
  [ApiErrorCode.INVALID_TOKEN]:
    'Missing or unknown bearer token. Request a new one at POST /v1/token.',
  [ApiErrorCode.TOKEN_EXPIRED]: 'Access token expired. Request a new one at POST /v1/token.',
  [ApiErrorCode.IP_NOT_ALLOWED]:
    'Requests from this IP address are not allowed for this integration.',
  [ApiErrorCode.INSUFFICIENT_SCOPE]: 'This integration is not allowed to access this resource.',
  [ApiErrorCode.NOT_FOUND]: 'Resource not found.',
  [ApiErrorCode.INVALID_REQUEST]: 'Invalid request parameters.',
  [ApiErrorCode.INVALID_PAGE_TOKEN]: 'Invalid or expired pageToken.',
  [ApiErrorCode.RATE_LIMITED]:
    'Too many requests. Retry after the interval in the Retry-After header.',
  [ApiErrorCode.METHOD_NOT_ALLOWED]: 'HTTP method not allowed for this route.',
  [ApiErrorCode.INTERNAL]: 'Internal error.',
};

/** Recursos da API — rótulo usado no log de uso e no escopo do cursor. */
export const ApiEndpoint = {
  TOKEN: 'token',
  TENANTS: 'tenants',
  PRODUCTS: 'products',
  LABELS: 'labels',
  USERS: 'users',
  /** `POST /v1/inbound/{endpointId}` — webhook de entrada do parceiro (#803). Entra no log de uso e no teto por integração. */
  INBOUND: 'inbound',
} as const;
export type ApiEndpoint = (typeof ApiEndpoint)[keyof typeof ApiEndpoint];
export const ALL_API_ENDPOINTS = Object.values(ApiEndpoint) as [ApiEndpoint, ...ApiEndpoint[]];
export const ApiEndpointSchema = z.enum(ALL_API_ENDPOINTS);

/**
 * IP ou faixa CIDR aceitos em `allowedIps`. Aceita IPv4/IPv6 com prefixo
 * opcional; a validação semântica (prefixo dentro do range da família) fica no
 * helper `isIpAllowed` das Functions, que é quem de fato compara.
 */
export const AllowedIpSchema = z
  .string()
  .trim()
  .min(2)
  .max(49)
  .refine(
    (v) => {
      const slash = v.indexOf('/');
      const addr = slash === -1 ? v : v.slice(0, slash);
      const isV6 = addr.includes(':');
      if (slash !== -1) {
        const prefix = v.slice(slash + 1);
        if (!/^\d{1,3}$/.test(prefix)) return false;
        if (Number(prefix) > (isV6 ? 128 : 32)) return false;
      }
      if (isV6) return /^[0-9a-fA-F:]+(\.\d{1,3}){0,3}$/.test(addr);
      const octets = addr.split('.');
      return (
        octets.length === 4 && octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255)
      );
    },
    { message: 'invalid_ip' },
  );

/**
 * Teto de contas com acesso à documentação por integração (issue #265).
 *
 * Baixo de propósito: é a lista de pessoas do parceiro que enxergam o contrato,
 * não uma lista de usuários do produto. Parceiro que precise de mais gente
 * provavelmente quer uma segunda integração, com alcance próprio.
 */
export const MAX_PORTAL_EMAILS_PER_KEY = 10;

/**
 * Conta Google autorizada a abrir `developers.etiquetachef.com` (issue #265).
 *
 * Normalizada em minúsculas porque o e-mail que chega no token do Firebase Auth
 * vem como o provedor mandou: comparar sem normalizar deixaria "Dev@parceiro.com"
 * de fora da própria allowlist onde ele foi cadastrado.
 */
export const PortalEmailSchema = z.string().trim().toLowerCase().pipe(z.email().max(200));

/**
 * Documento `/api_keys/{keyId}` — a integração.
 *
 * `createdAt`/`updatedAt`/`revokedAt` são Firestore `Timestamp` (tipados
 * `unknown`, materializados em millis por quem lê — mesmo padrão de
 * `AuditEventSchema.at`).
 */
export const ApiKeySchema = z.object({
  id: z.string(),
  /** Rótulo humano no admin — ex.: "foodgestor — produção". */
  name: z.string().min(1).max(80),
  /** Parceiro dono da integração — ex.: "foodgestor". Compõe o `clientId`. */
  partner: z.string().min(1).max(40),
  /** Identificador público da integração — ex.: `ec_foodgestor_a1b2c3d4`. */
  clientId: z.string().min(4).max(80),
  /** sha256 hex do `clientSecret`. O segredo em claro nunca persiste. */
  clientSecretHash: z.string().length(64),
  /**
   * Quais clientes a integração lê (#785). Avaliado a cada request contra
   * `tenants/{id}.partner_id` — ver `ApiTenantScope`.
   *
   * Obrigatório: a lista fixa `tenantIds` da #263 não tem mais leitor. As
   * integrações criadas nesse formato foram apagadas em produção em
   * 2026-09-30 (owner: recomeçar os testes do zero, #785), então não há
   * documento legado para tolerar — um que aparecesse falharia no parse e a
   * credencial deixaria de valer (fail-closed).
   */
  tenantScope: ApiTenantScopeSchema,
  /** `crm_partners/{id}` quando `tenantScope === PARTNER`; `null`/ausente nos demais. */
  scopePartnerId: ScopePartnerIdSchema.nullish(),
  /**
   * Demos liberados além da regra (#785, 2026-10-01). Ausente nas integrações
   * criadas antes — leitores tratam como `[]`.
   */
  demoTenantIds: DemoTenantIdsSchema.optional(),
  scopes: z.array(ApiScopeSchema).min(1),
  /** Vazio = sem filtro de origem. Não vazio = allowlist estrita. */
  allowedIps: z.array(AllowedIpSchema).max(20),
  /**
   * Contas Google que podem abrir o portal de desenvolvedores (#265).
   *
   * Vazio = ninguém do parceiro enxerga a documentação. É o default deliberado:
   * acesso à doc é concessão explícita, e revogar a integração revoga a doc no
   * mesmo gesto, sem uma segunda lista para alguém esquecer de limpar.
   *
   * `nullish` porque as integrações criadas antes da #265 não têm o campo.
   */
  portalEmails: z.array(PortalEmailSchema).max(MAX_PORTAL_EMAILS_PER_KEY).nullish(),
  requestsPerMinute: z
    .number()
    .int()
    .min(API_MIN_REQUESTS_PER_MINUTE)
    .max(API_MAX_REQUESTS_PER_MINUTE),
  active: z.boolean(),
  createdAt: z.unknown(),
  createdByUid: z.string(),
  updatedAt: z.unknown().nullish(),
  updatedByUid: z.string().nullish(),
  revokedAt: z.unknown().nullish(),
  revokedByUid: z.string().nullish(),
  /** Último request autenticado com sucesso (millis). Atualizado com folga. */
  lastUsedAtMillis: z.number().int().nullish(),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

/**
 * Documento `/api_tokens/{sha256(token)}` — o docId É o hash do token, então o
 * lookup do bearer é um `get()` direto, sem query nem índice.
 */
export const ApiTokenSchema = z.object({
  keyId: z.string().min(1),
  expiresAtMillis: z.number().int(),
  createdAt: z.unknown(),
  /** `expiresAt + 1h` — alvo da TTL policy nativa do Firestore. */
  expireAt: z.unknown(),
});
export type ApiToken = z.infer<typeof ApiTokenSchema>;

/**
 * Documento `/api_requests/{id}` — log de uso e base das janelas de rate
 * limit. `ipHash` é sha256(ip + salt): IP em claro nunca persiste (LGPD),
 * mesmo tratamento do audit de billing.
 */
export const ApiRequestLogSchema = z.object({
  /** `null` quando a credencial nem chegou a ser identificada. */
  keyId: z.string().nullable(),
  ipHash: z.string().nullable(),
  endpoint: ApiEndpointSchema,
  tenantId: z.string().nullable(),
  /**
   * HTTP status final. `null` = linha reservada pelo rate limit antes do
   * `count()` e ainda não fechada (#795): existe por milissegundos no caminho
   * normal, e fica `null` se o fechamento (`settleApiUsage`) falhar — a linha
   * continua contando na janela, só o status do log se perde.
   */
  status: z.number().int().nullable(),
  createdAt: z.unknown(),
  expireAt: z.unknown(),
});
export type ApiRequestLog = z.infer<typeof ApiRequestLogSchema>;

// ---------------------------------------------------------------------------
// DTOs de resposta
//
// Montados campo a campo a partir do doc do Firestore — NUNCA por spread. O
// domínio do EtiquetaChef é muito maior do que o que a integração precisa ver:
// ingredientes, alérgenos, fornecedores, dados de impressora, lote e autoria
// de cada transição ficam de fora por decisão, não por esquecimento.
// ---------------------------------------------------------------------------

export const PublicTenantDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    countryCode: z.string().nullable(),
    timeZone: z.string(),
  })
  .strict();
export type PublicTenantDto = z.infer<typeof PublicTenantDtoSchema>;

export const PublicValidityRuleDtoSchema = z
  .object({
    name: z.string(),
    durationHours: z.number().int(),
    isDefault: z.boolean(),
  })
  .strict();
export type PublicValidityRuleDto = z.infer<typeof PublicValidityRuleDtoSchema>;

export const PublicProductDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    category: z.enum(ALL_PRODUCT_CATEGORIES),
    unit: z.enum(ALL_UNITS_OF_MEASURE),
    active: z.boolean(),
    validityRules: z.array(PublicValidityRuleDtoSchema),
    /** Prepara sync incremental (`updatedAfter`) na v1.1. */
    createdAtMillis: z.number().int().nullable(),
    updatedAtMillis: z.number().int().nullable(),
  })
  .strict();
export type PublicProductDto = z.infer<typeof PublicProductDtoSchema>;

/**
 * Status de etiqueta expostos quando o request não filtra por `status`.
 * DRAFT (rascunho ainda não impresso) e DELETED (lixeira) nunca saem na v1 —
 * não são fato operacional para o parceiro.
 *
 * `MIXED` (#330) entra aqui **antes** de qualquer etiqueta poder assumi-lo:
 * é fato operacional (etiqueta baixada com mais de um desfecho) e, se
 * estivesse de fora, o `PublicLabelDtoSchema` — última linha de defesa da
 * saída — reprovaria o DTO e derrubaria a listagem inteira do parceiro no dia
 * em que a primeira `MIXED` existisse.
 */
export const PUBLIC_LABEL_STATUSES = [
  'PRINTED',
  'CONSUMED',
  'DISCARDED',
  'EXPIRED',
  'MIXED',
] as const;
export const PublicLabelStatusSchema = z.enum(PUBLIC_LABEL_STATUSES);
export type PublicLabelStatus = z.infer<typeof PublicLabelStatusSchema>;

/**
 * Uma entrada do ledger de baixas (épico #323), **sem identidade**.
 *
 * O ledger interno guarda quem executou cada movimento (`byUid`, `byName`),
 * de onde (`platform`, `via`) e o rastro do backfill (`migrationRunId`). Nada
 * disso sai: a API declara em `10-overview.md` que nunca devolve quem executou
 * uma mudança de estado de etiqueta, e o quadro de pessoal do cliente não é
 * fato operacional do parceiro.
 *
 * O array vai **cru**, com as entradas `UNDO` inclusas, e não só os desfechos
 * efetivos. Expondo só os efetivos, uma entrada *desapareceria* entre dois
 * polls quando alguém estornasse — e sync incremental sobre um histórico que
 * encolhe não tem como funcionar. Quem quer só o resultado lê os agregados, que
 * já vêm líquidos.
 */
export const PublicLabelBaixaDtoSchema = z
  .object({
    id: z.string(),
    kind: z.enum(ALL_LABEL_BAIXA_KINDS),
    quantity: z.number(),
    /** Quando o movimento aconteceu (relógio de quem registrou). */
    atMillis: z.number().int(),
    /** Só em `UNDO`: o `id` da entrada estornada. `null` nos desfechos. */
    reverses: z.string().nullable(),
  })
  .strict();
export type PublicLabelBaixaDto = z.infer<typeof PublicLabelBaixaDtoSchema>;

/**
 * Etiqueta em nível "resumo operacional": o que a etiqueta é, quando foi
 * manipulada, quando vence, em que estado está e de quem é a responsabilidade.
 *
 * `expiresAtMillis` é nullable de propósito: etiqueta criada offline chega ao
 * Firestore sem o carimbo, que só o trigger do servidor preenche. O parceiro
 * precisa tolerar null em vez de assumir que o campo sempre existe.
 *
 * **Quantidades (épico #323).** Uma etiqueta pode ser baixada em partes, então
 * `quantity` (o que foi impresso) deixou de ser o que ainda existe. Para
 * estoque, some `remainingQuantity`; somar `quantity` das vigentes
 * superestima. Os quatro derivados vêm do ledger já **líquido de estornos**, e
 * a identidade que sempre fecha é:
 *
 * ```
 * quantity + overrunQuantity
 *   === consumedQuantity + discardedQuantity + expiredQuantity + remainingQuantity
 * ```
 *
 * `overrunQuantity > 0` é anomalia auditável, não erro de leitura: duas baixas
 * offline concorrentes podem somar mais que o impresso, e o sistema registra o
 * excesso em vez de escondê-lo.
 */
export const PublicLabelDtoSchema = z
  .object({
    id: z.string(),
    productId: z.string(),
    productName: z.string(),
    ruleKind: z.string(),
    ruleDurationHours: z.number().int(),
    openedAtMillis: z.number().int(),
    expiresAtMillis: z.number().int().nullable(),
    // `PUBLIC_LABEL_STATUSES`, não o enum completo: a query já exclui DRAFT e
    // DELETED, e o schema de saída é a última linha de defesa — se um dia um
    // filtro mudar, o DTO barra em vez de vazar rascunho ou lixeira.
    status: PublicLabelStatusSchema,
    statusChangedAtMillis: z.number().int().nullable(),
    responsibleId: z.string(),
    responsibleName: z.string(),
    /** O que foi IMPRESSO. Imutável depois da impressão; não é o saldo. */
    quantity: z.number(),
    /** O saldo que ainda existe. É este que se soma para estimar estoque. */
    remainingQuantity: z.number(),
    consumedQuantity: z.number(),
    discardedQuantity: z.number(),
    expiredQuantity: z.number(),
    /** `max(0, movimentado − quantity)`; `0` no caso normal. */
    overrunQuantity: z.number(),
    /**
     * Histórico de movimentação, do mais antigo para o mais recente. Vazio na
     * etiqueta que nunca foi baixada. `MIXED` significa "fechou com mais de um
     * desfecho" — é aqui que se lê a composição.
     */
    baixas: z.array(PublicLabelBaixaDtoSchema).max(LABEL_BAIXAS_MAX),
    createdAtMillis: z.number().int().nullable(),
  })
  .strict();
export type PublicLabelDto = z.infer<typeof PublicLabelDtoSchema>;

export const PublicUserDtoSchema = z
  .object({
    id: z.string(),
    displayName: z.string(),
    role: z.enum(ALL_TENANT_ROLES),
  })
  .strict();
export type PublicUserDto = z.infer<typeof PublicUserDtoSchema>;

// ---------------------------------------------------------------------------
// DTOs dos recursos que só existem como webhook (#792 — 2026-10-02).
//
// Mesma regra dos DTOs acima: campo a campo, `.strict()`, e só o que é fato
// operacional para o parceiro. Contato de fornecedor (telefone, e-mail,
// WhatsApp), MAC/identidade BLE da impressora e autoria (`createdByUid`) ficam
// de fora por decisão — são dado pessoal ou detalhe de hardware, e nenhuma
// integração pediu. Montados por `apps/functions/src/publicApi/dto.ts`.
// ---------------------------------------------------------------------------

/** Fornecedor (`tenants/{tid}/suppliers/{id}`). */
export const PublicSupplierDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    active: z.boolean(),
    createdAtMillis: z.number().int().nullable(),
    updatedAtMillis: z.number().int().nullable(),
  })
  .strict();
export type PublicSupplierDto = z.infer<typeof PublicSupplierDtoSchema>;

/** Impressora cadastrada (`tenants/{tid}/printers/{id}`) — sem MAC nem identidade BLE. */
export const PublicPrinterDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    model: z.string().nullable(),
    active: z.boolean(),
    createdAtMillis: z.number().int().nullable(),
    updatedAtMillis: z.number().int().nullable(),
  })
  .strict();
export type PublicPrinterDto = z.infer<typeof PublicPrinterDtoSchema>;

/** Modelo de etiqueta (`tenants/{tid}/labelTemplates/{id}`) — sem o layout. */
export const PublicLabelTemplateDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    widthMm: z.number().int(),
    heightMm: z.number().int(),
    active: z.boolean(),
    isDefault: z.boolean(),
    createdAtMillis: z.number().int().nullable(),
    updatedAtMillis: z.number().int().nullable(),
  })
  .strict();
export type PublicLabelTemplateDto = z.infer<typeof PublicLabelTemplateDtoSchema>;

/** Responsável pela manipulação (`tenants/{tid}/responsibles/{id}`). */
export const PublicResponsibleDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    active: z.boolean(),
    isDefault: z.boolean(),
    createdAtMillis: z.number().int().nullable(),
  })
  .strict();
export type PublicResponsibleDto = z.infer<typeof PublicResponsibleDtoSchema>;

/**
 * Tenant no webhook: o `PublicTenantDto` mais o status operacional. O REST
 * (`GET /v1/tenants`) não expõe `status` — o webhook expõe porque
 * `tenant.suspended`/`tenant.reactivated` sem o estado seria evento sem fato.
 * Só os três estados que o parceiro pode ver; `DELETED` sai como tombstone.
 */
export const PublicWebhookTenantStatus = {
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;
export type PublicWebhookTenantStatus =
  (typeof PublicWebhookTenantStatus)[keyof typeof PublicWebhookTenantStatus];

export const PublicWebhookTenantDtoSchema = PublicTenantDtoSchema.extend({
  status: z.enum([PublicWebhookTenantStatus.ACTIVE, PublicWebhookTenantStatus.SUSPENDED]),
}).strict();
export type PublicWebhookTenantDto = z.infer<typeof PublicWebhookTenantDtoSchema>;

/** Resposta de `POST /v1/token` (nomes em snake_case, convenção OAuth2). */
export const ApiTokenResponseSchema = z
  .object({
    access_token: z.string(),
    token_type: z.literal('Bearer'),
    expires_in: z.number().int(),
    expires_at: z.string(),
  })
  .strict();
export type ApiTokenResponse = z.infer<typeof ApiTokenResponseSchema>;

/** Body de `POST /v1/token`. */
export const ApiTokenRequestSchema = z.object({
  client_id: z.string().min(1).max(80),
  client_secret: z.string().min(1).max(200),
});
export type ApiTokenRequest = z.infer<typeof ApiTokenRequestSchema>;

// ---------------------------------------------------------------------------
// Inputs das callables super-admin (`apps/admin` → `apps/functions/src/admin`)
// ---------------------------------------------------------------------------

export const CreateApiKeyInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  partner: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/, 'invalid_partner'),
  /** Quais clientes a integração lê (#785). */
  tenantScope: ApiTenantScopeSchema,
  /** Obrigatório com `PARTNER`, proibido nos demais (`refineTenantScope`). */
  scopePartnerId: ScopePartnerIdSchema.nullable().default(null),
  /** Demos liberados além da regra (#785). Cada um precisa ser demo. */
  demoTenantIds: DemoTenantIdsSchema.default([]),
  scopes: z.array(ApiScopeSchema).min(1),
  allowedIps: z.array(AllowedIpSchema).max(20).default([]),
  portalEmails: z.array(PortalEmailSchema).max(MAX_PORTAL_EMAILS_PER_KEY).default([]),
  requestsPerMinute: z
    .number()
    .int()
    .min(API_MIN_REQUESTS_PER_MINUTE)
    .max(API_MAX_REQUESTS_PER_MINUTE)
    .default(API_DEFAULT_REQUESTS_PER_MINUTE),
}).superRefine(refineTenantScope);
export type CreateApiKeyInput = z.infer<typeof CreateApiKeyInputSchema>;

/**
 * Retorno de `adminCreateApiKey`. `clientSecret` só existe aqui — some do
 * sistema no instante seguinte, e a UI avisa disso antes de fechar o modal.
 */
export const CreateApiKeyResultSchema = z.object({
  keyId: z.string(),
  clientId: z.string(),
  clientSecret: z.string(),
});
export type CreateApiKeyResult = z.infer<typeof CreateApiKeyResultSchema>;

export const UpdateApiKeyInputSchema = z.object({
  keyId: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(80).optional(),
  /**
   * Novo alcance (#785). Anda junto com `scopePartnerId`: mandar só o parceiro
   * é recusado.
   */
  tenantScope: ApiTenantScopeSchema.optional(),
  scopePartnerId: ScopePartnerIdSchema.nullable().optional(),
  /** Substitui a lista inteira de demos liberados (#785). Ausente = não muda. */
  demoTenantIds: DemoTenantIdsSchema.optional(),
  scopes: z.array(ApiScopeSchema).min(1).optional(),
  allowedIps: z.array(AllowedIpSchema).max(20).optional(),
  portalEmails: z.array(PortalEmailSchema).max(MAX_PORTAL_EMAILS_PER_KEY).optional(),
  requestsPerMinute: z
    .number()
    .int()
    .min(API_MIN_REQUESTS_PER_MINUTE)
    .max(API_MAX_REQUESTS_PER_MINUTE)
    .optional(),
  active: z.boolean().optional(),
}).superRefine(refineTenantScope);
export type UpdateApiKeyInput = z.infer<typeof UpdateApiKeyInputSchema>;

export const RevokeApiKeyInputSchema = z.object({
  keyId: z.string().min(1).max(64),
});
export type RevokeApiKeyInput = z.infer<typeof RevokeApiKeyInputSchema>;

/**
 * Remoção definitiva de uma integração **já revogada** (`adminDeleteApiKey`).
 *
 * Origem: #791 — 2026-10-01 13:44 +0200 (seleção múltipla e remoção em lote
 * em `/settings/api-keys`).
 *
 * Por quê: a revogação só marca `revokedAt`, e as revogadas se acumulavam na
 * tabela para sempre. Apagar só é aceito depois de revogar: a integração já
 * não tem acesso nenhum, e o rastro continua no `/audit`
 * (`API_KEY_REVOKED` + `API_KEY_DELETED`).
 *
 * Usado por: callable `adminDeleteApiKey` (Functions) e
 * `adminDeleteApiKeyCallable` (`apps/admin`, `ApiKeysPage`).
 */
export const DeleteApiKeyInputSchema = z.object({
  keyId: z.string().min(1).max(64),
});
export type DeleteApiKeyInput = z.infer<typeof DeleteApiKeyInputSchema>;

/**
 * Rotação do `client_secret` de uma integração existente.
 *
 * Existe porque o segredo **não é recuperável**: o banco guarda só o sha256, e
 * ele aparece uma única vez, na criação. Quem perdeu não consulta — rotaciona.
 * O `clientId` não muda (a integração é a mesma), mas o segredo anterior e os
 * access tokens emitidos com ele morrem no ato.
 */
export const RotateApiKeySecretInputSchema = z.object({
  keyId: z.string().min(1).max(64),
});
export type RotateApiKeySecretInput = z.infer<typeof RotateApiKeySecretInputSchema>;

/** Mesmo formato do retorno da criação — a UI reusa o modal de entrega. */
export const RotateApiKeySecretResultSchema = CreateApiKeyResultSchema;
export type RotateApiKeySecretResult = z.infer<typeof RotateApiKeySecretResultSchema>;
